import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchPublicSettings } from '../lib/publicSettingsCache';

/**
 * SEOManager — applies the site-wide SEO settings (title, description, OG, etc.)
 * to the document on every route change.
 *
 * Why on every route change? Other pages (Legal, KnowledgeBase, Blog, ...)
 * mutate `document.title` directly. When the user navigates back to the
 * homepage, we need to restore the site title — otherwise the tab keeps
 * showing the previous page's title.
 *
 * The settings fetch itself only runs once (cached in a ref) — only the
 * "apply to DOM" step re-runs.
 */

// Pages that own their own <title> and meta tags. We skip the global apply
// for these so we don't clobber per-page SEO.
const SKIP_TITLE_PATHS = [
  '/templates',
  '/privacy',
  '/terms',
  '/cookies',
  '/dpa',
  '/gdpr',
  '/knowledge-base',
  '/blog',
  '/tools',
  '/cli',
  '/press',
  '/trust',
  '/about',
  '/f/',
];

const upsertMeta = (selector, attr, value, builder) => {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = builder();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const SEOManager = () => {
  const { pathname } = useLocation();
  const seoRef = useRef(null);
  const fetchPromise = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const apply = (seo) => {
      if (cancelled || !seo) return;
      // Pages listed in SKIP_TITLE_PATHS also own their own <meta
      // description>, <og:title>, and <og:description>. This is the
      // single source of truth: if a route is in the list, the global
      // apply step is a no-op for those tags. Reported by Bing Webmaster
      // Tools as "Too many pages with identical meta descriptions"
      // when the site-default description was leaking onto every
      // /tools, /press, /trust, /knowledge-base, /blog page.
      const ownsPageSEO =
        SKIP_TITLE_PATHS.some((p) => pathname.startsWith(p)) ||
        window.__isNotFoundPage === true;

      if (seo.site_title && !ownsPageSEO) {
        document.title = seo.site_title;
      }

      if (seo.site_description && !ownsPageSEO) {
        upsertMeta('meta[name="description"]', 'content', seo.site_description, () => {
          const m = document.createElement('meta');
          m.setAttribute('name', 'description');
          return m;
        });
      }

      if (seo.meta_keywords) {
        upsertMeta('meta[name="keywords"]', 'content', seo.meta_keywords, () => {
          const m = document.createElement('meta');
          m.setAttribute('name', 'keywords');
          return m;
        });
      }

      if (seo.site_title && !ownsPageSEO) {
        upsertMeta('meta[property="og:title"]', 'content', seo.site_title, () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:title');
          return m;
        });
      }

      if (seo.site_description && !ownsPageSEO) {
        upsertMeta('meta[property="og:description"]', 'content', seo.site_description, () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:description');
          return m;
        });
      }

      if (seo.og_image_url) {
        upsertMeta('meta[property="og:image"]', 'content', seo.og_image_url, () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:image');
          return m;
        });
      }

      if (seo.twitter_handle) {
        upsertMeta('meta[name="twitter:site"]', 'content', seo.twitter_handle, () => {
          const m = document.createElement('meta');
          m.setAttribute('name', 'twitter:site');
          return m;
        });
      }
    };

    // Use cached settings if we already fetched them
    if (seoRef.current) {
      apply(seoRef.current);
      return () => { cancelled = true; };
    }

    // Share the module-level cache with usePublicSettings so we never
    // fetch /api/settings/public more than once per 5-minute window.
    if (!fetchPromise.current) {
      fetchPromise.current = fetchPublicSettings().then((data) => (data && data.seo) || null);
    }

    fetchPromise.current.then((seo) => {
      if (cancelled) return;
      seoRef.current = seo;
      apply(seo);
    });

    return () => { cancelled = true; };
  }, [pathname]);

  return null;
};

export default SEOManager;
